import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtDate } from "@/constants/labels";
import { daysUntil } from "@/lib/jalonHelpers";

/**
 * Panneau des dates de chantier — vue commune donneur d'ordre + prestataire.
 *
 * - Affiche démarrage prévu / fin prévisionnelle et l'indicateur de retard.
 * - Permet à chaque partie de proposer une nouvelle date de fin (motif ≥ 5c).
 * - L'autre partie voit la proposition et peut l'accepter ou la refuser.
 *
 * Attend en prop ``mission`` qui doit exposer ``id``, ``date_debut``,
 * ``date_fin``, ``date_fin_proposition``, ``chantier_statut``.
 */
export default function ChantierDatesPanel({ mission, role, onChanged }) {
  const [modalKind, setModalKind] = useState(null); // "propose" | "decide"
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ nouvelle_date_fin: "", motif: "" });
  const [decideMotif, setDecideMotif] = useState("");

  const dateDebut = mission?.date_debut;
  const dateFin = mission?.date_fin;
  const dRestants = daysUntil(dateFin ? `${dateFin}T23:59:59Z` : null);
  const enRetard = dateFin && dRestants !== null && dRestants < 0
    && mission?.statut !== "terminee" && mission?.statut !== "annulee";
  const retardNonJustifie = mission?.chantier_statut === "retard_non_justifie";
  const prop = mission?.date_fin_proposition;
  const propPending = prop && prop.statut === "en_attente";
  const iAmProposer = propPending && prop.proposed_by
    && ((role === "entreprise" && prop.proposed_by_role === "entreprise")
        || (role === "prestataire" && prop.proposed_by_role === "prestataire"));

  const submitPropose = async () => {
    if (!form.nouvelle_date_fin) return toast.error("Sélectionnez une nouvelle date de fin");
    if ((form.motif || "").trim().length < 5) return toast.error("Motif obligatoire (min 5 caractères)");
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/date-fin/proposer`, form);
      toast.success("Nouvelle date proposée — en attente d'acceptation de l'autre partie");
      setModalKind(null); setForm({ nouvelle_date_fin: "", motif: "" });
      onChanged?.();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/date-fin/decider`,
                     { decision, motif: decideMotif || null });
      toast.success(decision === "acceptee" ? "Nouvelle date acceptée" : "Proposition refusée");
      setModalKind(null); setDecideMotif("");
      onChanged?.();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E4E7EB] rounded-[6px] p-3 bg-white space-y-3" data-testid={`chantier-dates-${mission?.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-[#1B3A5C]" />
          <span className="font-semibold text-sm text-[#1F2933]">Dates du chantier</span>
        </div>
        {retardNonJustifie && (
          <span className="badge-danger" data-testid={`chantier-retard-non-justifie-${mission.id}`}>
            <XCircle size={12} /> Retard non justifié
          </span>
        )}
        {!retardNonJustifie && enRetard && (
          <span className="badge-warn" data-testid={`chantier-en-retard-${mission.id}`}>
            <AlertTriangle size={12} /> En retard de {-dRestants} jour(s)
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase text-[#3E4C59]">Démarrage prévu</div>
          <div className="text-[#1F2933]" data-testid={`chantier-date-debut-${mission.id}`}>
            {dateDebut ? fmtDate(dateDebut) : "—"}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-[#3E4C59]">Fin prévisionnelle</div>
          <div className="text-[#1F2933]" data-testid={`chantier-date-fin-${mission.id}`}>
            {dateFin ? fmtDate(dateFin) : "—"}
            {dateFin && !enRetard && mission?.statut !== "terminee" && dRestants !== null && (
              <span className="ml-2 text-xs text-[#3E4C59]">
                <Clock size={10} className="inline" /> {dRestants >= 0 ? `dans ${dRestants} j` : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {retardNonJustifie && (
        <div className="border-l-4 border-[#C0392B] bg-[#FEF2F2] p-3 text-[13px]">
          <p className="font-semibold text-[#1F2933] mb-1 inline-flex items-center gap-1"><XCircle size={14} /> Retard non justifié</p>
          <p>Sept jours ouvrés se sont écoulés après la date de fin sans déclaration d'achèvement ni proposition de nouvelle date acceptée. La mission est visible en retard non justifié pour les deux parties jusqu'à régularisation.</p>
        </div>
      )}

      {propPending && (
        <div className="border-l-4 border-[#E8710A] bg-[#FEF5EA] p-3 text-[13px]" data-testid={`chantier-proposition-${mission.id}`}>
          <p className="font-semibold text-[#1F2933] mb-1 inline-flex items-center gap-1">
            <CalendarClock size={14} /> Nouvelle date de fin proposée : <b>{fmtDate(prop.nouvelle_date_fin)}</b>
          </p>
          <p><i>Par</i> : {prop.proposed_by_role} · <i>Motif</i> : « {prop.motif} »</p>
          {iAmProposer ? (
            <p className="text-xs text-[#3E4C59] mt-1">En attente de la décision de l'autre partie.</p>
          ) : (
            <div className="mt-2 flex gap-2 flex-wrap">
              <button onClick={() => setModalKind("decide-accept")} disabled={busy}
                      className="btn-primary text-xs" data-testid={`chantier-accept-${mission.id}`}>
                <CheckCircle2 size={12} /> Accepter
              </button>
              <button onClick={() => setModalKind("decide-refuse")} disabled={busy}
                      className="btn-outline text-xs" data-testid={`chantier-refuse-${mission.id}`}>
                <XCircle size={12} /> Refuser
              </button>
            </div>
          )}
        </div>
      )}

      {!propPending && mission?.statut !== "terminee" && mission?.statut !== "annulee" && (
        <div className="pt-1 border-t border-[#E4E7EB]">
          <button onClick={() => setModalKind("propose")} disabled={busy}
                  className="btn-outline text-xs" data-testid={`chantier-propose-btn-${mission.id}`}>
            <CalendarClock size={12} /> Proposer une nouvelle date de fin
          </button>
        </div>
      )}

      {modalKind === "propose" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EB] bg-[#F5F6F7]">
              <h2 className="text-lg font-semibold text-[#1F2933]">Proposer une nouvelle date de fin</h2>
              <button onClick={() => setModalKind(null)} className="btn-ghost text-sm">✕</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <p className="text-[#3E4C59]">Date actuelle : <b>{dateFin ? fmtDate(dateFin) : "—"}</b>. La modification n'entre en vigueur qu'après acceptation de l'autre partie.</p>
              <div>
                <label className="label">Nouvelle date de fin <span className="text-[#C0392B]">*</span></label>
                <input type="date" className="input" required
                       min={dateDebut || undefined}
                       value={form.nouvelle_date_fin}
                       onChange={(e) => setForm({ ...form, nouvelle_date_fin: e.target.value })}
                       data-testid={`chantier-propose-date-${mission.id}`} />
              </div>
              <div>
                <label className="label">Motif (obligatoire, min 5 c.) <span className="text-[#C0392B]">*</span></label>
                <textarea rows={3} minLength={5} maxLength={1000}
                          className="textarea"
                          placeholder="Ex : décalage suite intempéries la semaine du 12/03, matériaux livrés avec retard…"
                          value={form.motif}
                          onChange={(e) => setForm({ ...form, motif: e.target.value })}
                          data-testid={`chantier-propose-motif-${mission.id}`} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
                <button onClick={() => setModalKind(null)} className="btn-ghost text-sm">Annuler</button>
                <button onClick={submitPropose} disabled={busy} className="btn-primary" data-testid={`chantier-propose-submit-${mission.id}`}>
                  {busy ? "…" : "Proposer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(modalKind === "decide-accept" || modalKind === "decide-refuse") && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto">
          <div className="bg-white w-full max-w-lg rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EB] bg-[#F5F6F7]">
              <h2 className="text-lg font-semibold text-[#1F2933]">
                {modalKind === "decide-accept" ? "Accepter la nouvelle date" : "Refuser la proposition"}
              </h2>
              <button onClick={() => setModalKind(null)} className="btn-ghost text-sm">✕</button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <p className="text-[#1F2933]">
                Nouvelle date proposée : <b>{fmtDate(prop?.nouvelle_date_fin)}</b> — motif : « {prop?.motif} ».
              </p>
              {modalKind === "decide-refuse" && (
                <div>
                  <label className="label">Motif du refus (optionnel)</label>
                  <textarea rows={2} maxLength={1000}
                            className="textarea"
                            value={decideMotif}
                            onChange={(e) => setDecideMotif(e.target.value)} />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
                <button onClick={() => setModalKind(null)} className="btn-ghost text-sm">Annuler</button>
                <button
                  onClick={() => decide(modalKind === "decide-accept" ? "acceptee" : "refusee")}
                  disabled={busy}
                  className={modalKind === "decide-accept" ? "btn-primary" : "btn-danger"}
                  data-testid={`chantier-decide-confirm-${mission.id}`}
                >
                  {busy ? "…" : (modalKind === "decide-accept" ? "Confirmer l'acceptation" : "Confirmer le refus")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
